/**
 *
 * Posts to the shop's own domain, so this is same-origin: no CORS, no
 * API key in the page. Shopify signs the request on the way through and
 * the Worker verifies that signature before calling Attentive.
 *
 * Expects a form like:
 *   <form data-attentive-subscribe data-country-code="+1">
 *     <input name="phone" type="tel" required>
 *     <input name="email" type="email">
 *     <button type="submit">Sign up</button>
 *     <p data-attentive-message hidden></p>
 *   </form>
 */

// Must match [app_proxy] prefix + subpath in shopify.app.toml. These
// freeze on the store at install time, so changing them here alone
// is not enough.
const PROXY_PATH = "/apps/attentive/subscribe";

/**
 * Coerce shopper input toward E.164, which is all Attentive accepts.
 *
 * The country code is merchant config, not inference -- it is applied only
 * when the shopper did not supply one. Guessing a country for a bare
 * number would silently subscribe a real but different person.
 */
export function normalizePhone(raw, countryCode) {
  const phone = (raw || "").toString().trim();
  if (!phone) return "";

  // An explicit "+" is the shopper stating their own country code. Keep it,
  // but still strip formatting -- "+1 718 501 9212" is a number a shopper
  // will reasonably type, and it has to survive validation.
  if (phone.startsWith("+")) return "+" + phone.slice(1).replace(/\D/g, "");

  const cc = (countryCode || "").trim();
  if (!cc) return phone;

  // Strip formatting first so the checks below see only digits.
  let digits = phone.replace(/\D/g, "").replace(/^0+/, "");
  const ccDigits = cc.replace(/\D/g, "");

  // A shopper who typed the country code without "+" ("1 718 501 9212")
  // must not get it prepended twice. Only strip a leading match when what
  // remains is still a plausible subscriber number.
  if (digits.startsWith(ccDigits) && digits.length > ccDigits.length + 6) {
    digits = digits.slice(ccDigits.length);
  }

  return cc + digits;
}

/**
 * Reject numbers that cannot be right, so the shopper can fix them while
 * they are still on the page. Returns an error string, or null to allow.
 *
 * Only +1 is enforced, because NANP is the one numbering plan short enough
 * to hardcode honestly: exactly 10 digits after the country code. Other
 * country codes pass through unchecked rather than being guessed at -- a
 * wrong rule here would block real subscribers. If this store ever sells
 * into several countries, replace this with libphonenumber-js.
 */
export function phoneError(phone, countryCode) {
  if (!phone) return null;
  const cc = (countryCode || "").trim();
  if (cc !== "+1" || !phone.startsWith("+1")) return null;
  return /^\+1\d{10}$/.test(phone) ? null : "Enter a 10-digit phone number.";
}

function setMessage(form, text, ok) {
  const el = form.querySelector("[data-attentive-message]");
  if (!el) return;
  el.textContent = text;
  el.hidden = false;
  el.dataset.state = ok ? "ok" : "error";
}

async function submit(event, form) {
  event.preventDefault();
  const button = form.querySelector('button[type="submit"]');
  const data = new FormData(form);

  const phone = normalizePhone(data.get("phone"), form.dataset.countryCode);
  const email = (data.get("email") || "").toString().trim();
  if (!phone && !email) {
    setMessage(form, "Enter a phone number or email.", false);
    return;
  }

  const badPhone = phoneError(phone, form.dataset.countryCode);
  if (badPhone) {
    setMessage(form, badPhone, false);
    return;
  }

  if (button) button.disabled = true;
  try {
    const res = await fetch(PROXY_PATH, {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({ phone, email }),
    });

    // The Worker returns 202 on success, mirroring Attentive.
    if (res.status === 202) {
      setMessage(form, "Thanks -- check your phone to confirm.", true);
      form.reset();
      return;
    }

    // Everything else is deliberately vague to the shopper; the real
    // cause is in the Worker logs (npm run tail).
    const body = await res.json().catch(() => ({}));
    if (res.status === 400 && body.error === "phone_or_email_required") {
      setMessage(form, "Enter a phone number or email.", false);
    } else {
      setMessage(form, "Sorry, that didn't go through. Please try again.", false);
      console.error("subscribe failed", res.status, body);
    }
  } catch (err) {
    setMessage(form, "Network error. Please try again.", false);
    console.error("subscribe error", err);
  } finally {
    if (button) button.disabled = false;
  }
}

// Delegated so load order does not matter and re-rendered sections (theme
// editor) keep working without rebinding.
if (typeof document !== "undefined") {
  document.addEventListener("submit", (event) => {
    const form = event.target.closest?.("[data-attentive-subscribe]");
    if (form) submit(event, form);
  });
}